import type { AppRole, EmployeeStatus } from "./auth";
import { logAdminAction } from "./auditLog";
import { supabase } from "./supabase";
import type { CompetitionStatus } from "../types/database";

export type PlatformStats = {
  students: number;
  clients: number;
  companies: number;
  competitions: number;
  open_competitions: number;
  submissions: number;
  active_judges: number;
};

export type AdminStudentListRow = {
  id: string;
  full_name: string | null;
  email: string | null;
  university: string | null;
  major: string | null;
  graduation_year: number | null;
  completed_competitions: number;
};

export type AdminCompanyListRow = {
  id: string;
  name: string;
  size: string | null;
  industry: string | null;
  engagement_model: string | null;
  user_count: number;
  competition_count: number;
};

export type AdminCompetitionListRow = {
  id: string;
  name: string;
  company_id: string;
  company_name: string | null;
  status: CompetitionStatus;
  opens_at: string | null;
  closes_at: string | null;
};

export type AdminUserRow = {
  id: string;
  email: string | null;
  full_name: string | null;
  roles: AppRole[];
  signed_up_at: string | null;
};

export type AdminCompanyMemberRow = {
  user_id: string;
  role: "owner" | "member";
  email: string | null;
  full_name: string | null;
};

export type AdminAllowedDomainRow = {
  domain: string;
  university_name: string;
};

export type AdminCampusAdminRow = {
  user_id: string;
  email: string | null;
  full_name: string | null;
  university_name: string;
};

export type AutoMergeResult = {
  saved_teams: number;
  dissolved_teams: number;
  disqualified_students: number;
  members_moved: number;
};

export async function getPlatformStats(): Promise<PlatformStats> {
  const { data, error } = await supabase.rpc("admin_platform_stats");
  if (error || !data?.[0]) throw new Error(error?.message ?? "Failed");
  return data[0] as PlatformStats;
}

export async function setUserRole(userId: string, role: AppRole | null): Promise<void> {
  const { error } = await supabase.rpc("admin_set_role", { p_user_id: userId, p_role: role });
  if (error) throw new Error(error.message);
  await logAdminAction({
    action: "role.set",
    entityType: "user_profiles",
    entityId: userId,
    summary: `Set role to ${role ?? "(no role)"} for ${userId.slice(0, 8)}…`,
    diff: { after: { role } },
  });
}

export async function addUserToCompany(
  userId: string,
  companyId: string,
  companyRole: "owner" | "member" = "member"
): Promise<void> {
  const { error } = await supabase.rpc("admin_add_user_to_company", {
    p_user_id: userId,
    p_company_id: companyId,
    p_role: companyRole,
  });
  if (error) throw new Error(error.message);
}

export async function setCompetitionStatus(competitionId: string, status: CompetitionStatus): Promise<void> {
  const { error } = await supabase.rpc("admin_set_competition_status", {
    p_competition_id: competitionId,
    p_status: status,
  });
  if (error) throw new Error(error.message);
  await logAdminAction({
    action: "competition.force_status",
    entityType: "competitions",
    entityId: competitionId,
    summary: `Forced competition status to ${status}`,
    diff: { after: { status } },
  });
  void import("./analytics").then(({ track }) =>
    track("competition_status_changed", { competition_id: competitionId, status })
  );
}

export async function runCompetitionAutoMerge(
  competitionId: string
): Promise<AutoMergeResult> {
  const { data, error } = await supabase.rpc("admin_run_auto_merge", {
    p_competition_id: competitionId,
  });
  if (error) throw new Error(error.message);
  const row = (Array.isArray(data) ? data[0] : data) as
    | Partial<AutoMergeResult>
    | null
    | undefined;
  return {
    saved_teams: Number(row?.saved_teams ?? 0),
    dissolved_teams: Number(row?.dissolved_teams ?? 0),
    disqualified_students: Number(row?.disqualified_students ?? 0),
    members_moved: Number(row?.members_moved ?? 0),
  };
}

export async function listAllStudents(): Promise<AdminStudentListRow[]> {
  const [{ data: roleRows }, { data: profileRows }, { data: studentRows }, { data: regRows }] = await Promise.all([
    supabase.from("user_roles").select("user_id").eq("role", "student"),
    supabase.from("user_profiles").select("id, full_name, email, created_at"),
    supabase.from("students").select("id, university, major, graduation_year"),
    supabase.from("registrations").select("student_id, competition_id").eq("status", "submitted"),
  ]);

  const studentIds = new Set((roleRows ?? []).map((r) => r.user_id as string));
  const profileById = new Map((profileRows ?? []).map((r) => [r.id as string, r]));
  const studentById = new Map((studentRows ?? []).map((r) => [r.id as string, r]));
  const completed = new Map<string, Set<string>>();
  for (const row of regRows ?? []) {
    const sid = row.student_id as string;
    const cid = row.competition_id as string;
    if (!completed.has(sid)) completed.set(sid, new Set());
    completed.get(sid)?.add(cid);
  }

  return Array.from(studentIds).map((id) => {
    const p = profileById.get(id) as Record<string, unknown> | undefined;
    const s = studentById.get(id) as Record<string, unknown> | undefined;
    return {
      id,
      full_name: (p?.full_name as string | null | undefined) ?? null,
      email: (p?.email as string | null | undefined) ?? null,
      university: (s?.university as string | null | undefined) ?? null,
      major: (s?.major as string | null | undefined) ?? null,
      graduation_year: (s?.graduation_year as number | null | undefined) ?? null,
      completed_competitions: completed.get(id)?.size ?? 0,
    };
  });
}

/**
 * Create a "cold" company — Tyler uses this during a discovery call before
 * the prospect has a user account. We don't insert anything into
 * `company_users` here; the client-invite flow does that on accept.
 */
export async function createCompany(input: {
  name: string;
  contact_name?: string | null;
  contact_email?: string | null;
  industry?: string | null;
  size?: string | null;
}): Promise<{ id: string | null; error: Error | null }> {
  const { data, error } = await supabase
    .from("companies")
    .insert({
      name: input.name.trim(),
      contact_name: input.contact_name?.trim() || null,
      primary_contact_email: input.contact_email?.trim() || null,
      industry: input.industry?.trim() || null,
      size: input.size || null,
      // Default the company-level engagement to "rm" since that's the
      // entry point we use this from. Per-program engagement is stored
      // on `competitions.engagement_model`.
      engagement_model: "rm",
    })
    .select("id")
    .single();
  if (error || !data?.id) {
    return { id: null, error: new Error(error?.message ?? "Insert failed") };
  }
  return { id: data.id as string, error: null };
}

export async function listAllCompanies(): Promise<AdminCompanyListRow[]> {
  const [{ data: companies }, { data: companyUsers }, { data: competitions }] = await Promise.all([
    supabase.from("companies").select("id, name, size, industry, engagement_model").order("name", { ascending: true }),
    supabase.from("company_users").select("company_id"),
    supabase.from("competitions").select("company_id"),
  ]);

  const userCount = new Map<string, number>();
  for (const row of companyUsers ?? []) {
    const id = row.company_id as string;
    userCount.set(id, (userCount.get(id) ?? 0) + 1);
  }
  const competitionCount = new Map<string, number>();
  for (const row of competitions ?? []) {
    const id = row.company_id as string;
    competitionCount.set(id, (competitionCount.get(id) ?? 0) + 1);
  }

  return (companies ?? []).map((c) => ({
    id: c.id as string,
    name: (c.name as string) ?? "Company",
    size: (c.size as string | null | undefined) ?? null,
    industry: (c.industry as string | null | undefined) ?? null,
    engagement_model: (c.engagement_model as string | null | undefined) ?? null,
    user_count: userCount.get(c.id as string) ?? 0,
    competition_count: competitionCount.get(c.id as string) ?? 0,
  }));
}

export async function listAllCompetitions(): Promise<AdminCompetitionListRow[]> {
  const [compsRes, companiesRes] = await Promise.all([
    supabase
      .from("competitions")
      .select("id, name, company_id, status, opens_at, closes_at")
      .order("created_at", { ascending: false }),
    supabase.from("companies").select("id, name"),
  ]);

  const nameByCompany = new Map((companiesRes.data ?? []).map((c) => [c.id as string, (c.name as string) ?? null]));
  return (compsRes.data ?? []).map((c) => ({
    id: c.id as string,
    name: (c.name as string) ?? "Competition",
    company_id: c.company_id as string,
    company_name: nameByCompany.get(c.company_id as string) ?? null,
    status: c.status as CompetitionStatus,
    opens_at: (c.opens_at as string | null | undefined) ?? null,
    closes_at: (c.closes_at as string | null | undefined) ?? null,
  }));
}

export async function listAllUsers(): Promise<AdminUserRow[]> {
  const [{ data: profiles }, { data: roles }] = await Promise.all([
    supabase.from("user_profiles").select("id, full_name, email, created_at").order("created_at", { ascending: false }),
    supabase.from("user_roles").select("user_id, role"),
  ]);

  const rolesById = new Map<string, AppRole[]>();
  for (const row of roles ?? []) {
    const id = row.user_id as string;
    const role = row.role as AppRole;
    const list = rolesById.get(id) ?? [];
    if (!list.includes(role)) list.push(role);
    rolesById.set(id, list);
  }

  return (profiles ?? []).map((p) => ({
    id: p.id as string,
    email: (p.email as string | null | undefined) ?? null,
    full_name: (p.full_name as string | null | undefined) ?? null,
    roles: rolesById.get(p.id as string) ?? [],
    signed_up_at: (p.created_at as string | null | undefined) ?? null,
  }));
}

export async function listCompanyMembers(companyId: string): Promise<AdminCompanyMemberRow[]> {
  const { data: companyUsers } = await supabase
    .from("company_users")
    .select("user_id, role")
    .eq("company_id", companyId);
  if (!companyUsers?.length) return [];

  const ids = companyUsers.map((u) => u.user_id as string);
  const { data: profiles } = await supabase.from("user_profiles").select("id, full_name, email").in("id", ids);
  const profileById = new Map((profiles ?? []).map((p) => [p.id as string, p]));

  return companyUsers.map((cu) => {
    const pid = cu.user_id as string;
    const p = profileById.get(pid) as Record<string, unknown> | undefined;
    return {
      user_id: pid,
      role: ((cu.role as "owner" | "member" | undefined) ?? "member"),
      email: (p?.email as string | null | undefined) ?? null,
      full_name: (p?.full_name as string | null | undefined) ?? null,
    };
  });
}

export async function findUserByEmail(email: string): Promise<{ id: string; full_name: string | null } | null> {
  const { data } = await supabase
    .from("user_profiles")
    .select("id, full_name, email")
    .ilike("email", email.trim())
    .maybeSingle();
  if (!data?.id) return null;
  return { id: data.id as string, full_name: (data.full_name as string | null | undefined) ?? null };
}

export async function listAllowedDomains(): Promise<AdminAllowedDomainRow[]> {
  const { data, error } = await supabase
    .from("allowed_email_domains")
    .select("domain, university_name")
    .order("university_name", { ascending: true });
  if (error || !data) return [];
  return data as AdminAllowedDomainRow[];
}

export async function addAllowedDomain(domain: string, universityName: string): Promise<void> {
  const norm = domain.trim().toLowerCase();
  const uni = universityName.trim();
  const { error } = await supabase
    .from("allowed_email_domains")
    .insert({ domain: norm, university_name: uni });
  if (error) throw new Error(error.message);
  await logAdminAction({
    action: "domain.add",
    entityType: "allowed_email_domains",
    summary: `Added ${norm} (${uni})`,
    diff: { after: { domain: norm, university_name: uni } },
  });
}

export async function removeAllowedDomain(domain: string): Promise<void> {
  const { error } = await supabase.from("allowed_email_domains").delete().eq("domain", domain);
  if (error) throw new Error(error.message);
  await logAdminAction({
    action: "domain.remove",
    entityType: "allowed_email_domains",
    summary: `Removed ${domain}`,
    diff: { before: { domain } },
  });
}

export async function listCampusAdmins(): Promise<AdminCampusAdminRow[]> {
  const { data: campusRows } = await supabase
    .from("campus_admins")
    .select("user_id, university_name")
    .order("university_name", { ascending: true });
  if (!campusRows?.length) return [];

  const userIds = campusRows.map((r) => r.user_id as string);
  const { data: profiles } = await supabase
    .from("user_profiles")
    .select("id, full_name, email")
    .in("id", userIds);
  const profileById = new Map((profiles ?? []).map((p) => [p.id as string, p]));

  return campusRows.map((row) => {
    const p = profileById.get(row.user_id as string) as Record<string, unknown> | undefined;
    return {
      user_id: row.user_id as string,
      university_name: (row.university_name as string) ?? "",
      email: (p?.email as string | null | undefined) ?? null,
      full_name: (p?.full_name as string | null | undefined) ?? null,
    };
  });
}

export async function provisionCampusAdmin(userId: string, universityName: string): Promise<void> {
  const uni = universityName.trim();
  const { error } = await supabase.rpc("admin_provision_campus_admin", {
    p_user_id: userId,
    p_university_name: uni,
  });
  if (error) throw new Error(error.message);
  await logAdminAction({
    action: "campus_admin.provision",
    entityType: "campus_admins",
    entityId: userId,
    summary: `Provisioned campus admin for ${uni}`,
    diff: { after: { user_id: userId, university_name: uni } },
  });
}

export async function removeCampusAdmin(userId: string): Promise<void> {
  const { error } = await supabase.rpc("admin_remove_campus_admin", { p_user_id: userId });
  if (error) throw new Error(error.message);
  await logAdminAction({
    action: "campus_admin.remove",
    entityType: "campus_admins",
    entityId: userId,
    summary: `Removed campus admin ${userId.slice(0, 8)}…`,
    diff: { before: { user_id: userId } },
  });
}

// ─── Admin team / employee lifecycle (Day 23d) ─────────────────────────────

export type SessionRow = {
  session_id: string;
  created_at: string;
  updated_at: string;
  user_agent: string | null;
  ip: string | null;
};

/** Admin directory row (`user_profiles` + primary admin `user_roles`). */
export type AdminTeamUserRow = {
  id: string;
  email: string | null;
  full_name: string | null;
  avatar_url: string | null;
  role: AppRole;
  employee_status: EmployeeStatus;
  admin_permissions: string[] | null;
  role_assigned_at: string | null;
  profile_created_at: string | null;
};

const ADMIN_PORTAL_ROLES: AppRole[] = ["super_admin", "admin_rm", "admin_licensing"];

function coerceEmployeeStatus(value: unknown): EmployeeStatus {
  if (value === "pending" || value === "rejected") return value;
  return "active";
}

function unwrapRpcRows<T>(data: unknown): T[] {
  if (data == null) return [];
  if (Array.isArray(data)) return data as T[];
  if (typeof data === "object" && data !== null && "sessions" in data && Array.isArray((data as { sessions: unknown }).sessions)) {
    return (data as { sessions: T[] }).sessions;
  }
  return [];
}

export async function listAdminUsers(filter?: { status?: EmployeeStatus }): Promise<AdminTeamUserRow[]> {
  const { data: roleRows } = await supabase
    .from("user_roles")
    .select("user_id, role, created_at")
    .in("role", ADMIN_PORTAL_ROLES)
    .order("created_at", { ascending: false });
  if (!roleRows?.length) return [];

  const bestByUser = new Map<string, { role: AppRole; created_at: string | null }>();
  for (const row of roleRows) {
    const uid = row.user_id as string;
    if (bestByUser.has(uid)) continue;
    const r = row.role as AppRole;
    if (!ADMIN_PORTAL_ROLES.includes(r)) continue;
    bestByUser.set(uid, {
      role: r,
      created_at: (row.created_at as string | null | undefined) ?? null,
    });
  }

  const userIds = [...bestByUser.keys()];
  const { data: profiles } = await supabase
    .from("user_profiles")
    .select("id, email, full_name, avatar_url, employee_status, admin_permissions, created_at")
    .in("id", userIds);
  const profileById = new Map((profiles ?? []).map((p) => [p.id as string, p]));

  const out: AdminTeamUserRow[] = [];
  for (const uid of userIds) {
    const p = profileById.get(uid) as Record<string, unknown> | undefined;
    const br = bestByUser.get(uid)!;
    const es = coerceEmployeeStatus(p?.employee_status);
    const perms = p?.admin_permissions;
    const adminPerms = Array.isArray(perms) ? (perms as string[]) : null;
    if (filter?.status && es !== filter.status) continue;
    out.push({
      id: uid,
      email: (p?.email as string | null | undefined) ?? null,
      full_name: (p?.full_name as string | null | undefined) ?? null,
      avatar_url: (p?.avatar_url as string | null | undefined) ?? null,
      role: br.role,
      employee_status: es,
      admin_permissions: adminPerms,
      role_assigned_at: br.created_at,
      profile_created_at: (p?.created_at as string | null | undefined) ?? null,
    });
  }
  return out;
}

export async function getPendingAdminTeamCount(): Promise<number> {
  const rows = await listAdminUsers({ status: "pending" });
  return rows.length;
}

export async function hasAdminPermission(permission: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("has_admin_permission", { p_perm: permission });
  if (error) return false;
  return Boolean(data);
}

export async function approveEmployee(userId: string): Promise<void> {
  const { error } = await supabase.rpc("admin_approve_employee", { p_user_id: userId });
  if (error) throw new Error(error.message);
  await logAdminAction({
    action: "team.approve",
    entityType: "user_profiles",
    entityId: userId,
    summary: `Approved team member ${userId.slice(0, 8)}…`,
  });
}

export async function rejectEmployee(userId: string): Promise<void> {
  const { error } = await supabase.rpc("admin_reject_employee", { p_user_id: userId });
  if (error) throw new Error(error.message);
  await logAdminAction({
    action: "team.reject",
    entityType: "user_profiles",
    entityId: userId,
    summary: `Rejected team member ${userId.slice(0, 8)}…`,
  });
}

export async function setUserPermissions(userId: string, perms: string[]): Promise<void> {
  const { error } = await supabase.rpc("admin_set_user_permissions", {
    p_user_id: userId,
    p_permissions: perms,
  });
  if (error) throw new Error(error.message);
}

export async function forceSignOutUser(userId: string): Promise<void> {
  const { error } = await supabase.rpc("admin_force_signout", { p_user_id: userId });
  if (error) throw new Error(error.message);
}

export async function lockUser(userId: string, reason?: string): Promise<void> {
  const { error } = await supabase.rpc("admin_lock_user", {
    p_user_id: userId,
    p_reason: reason ?? null,
  });
  if (error) throw new Error(error.message);
}

export async function unlockUser(userId: string): Promise<void> {
  const { error } = await supabase.rpc("admin_unlock_user", { p_user_id: userId });
  if (error) throw new Error(error.message);
}

export async function resetUserMfa(userId: string): Promise<void> {
  const { error } = await supabase.rpc("admin_reset_user_mfa", { p_user_id: userId });
  if (error) throw new Error(error.message);
}

/* ───── Admin dashboard helpers (Day-25 admin polish) ───────────────────── */

export type AdminDashboardSnapshot = {
  /** Current totals (mirrors admin_platform_stats). */
  total_students: number;
  total_active_judges: number;
  total_open_competitions: number;
  /** Last-7-day deltas (count of rows created in the last 7 days). */
  new_students_7d: number;
  new_active_judges_7d: number;
  new_open_competitions_7d: number;
  /** Queue counts that need attention. */
  inquiries_new: number;
  pending_team: number;
  discovery_calls_this_week: number;
  drafts_stale_gt_7d: number;
};

/**
 * Composite snapshot for the admin dashboard. Keeps the request count low
 * by parallelizing six count queries — no separate RPC needed. All counts
 * trust RLS; admins read everything.
 */
export async function getAdminDashboardSnapshot(): Promise<AdminDashboardSnapshot> {
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 86_400_000);
  const inAWeek = new Date(now.getTime() + 7 * 86_400_000);
  const weekAgoIso = weekAgo.toISOString();
  const nowIso = now.toISOString();
  const inAWeekIso = inAWeek.toISOString();

  // Pull totals + deltas + queues in parallel.
  const [
    totalsRes,
    studentsAllRes,
    studentsNewRes,
    judgesAllRes,
    judgesNewRes,
    compsOpenRes,
    compsOpenNewRes,
    inquiriesNewRes,
    pendingTeam,
    callsThisWeekRes,
    staleDraftsRes,
  ] = await Promise.all([
    // Pull total counts in one shot via admin_platform_stats so we stay
    // consistent with what the RPC reports elsewhere.
    supabase.rpc("admin_platform_stats"),
    // Student deltas: count rows in user_roles where role='student'.
    supabase.from("user_roles").select("*", { count: "exact", head: true }).eq("role", "student"),
    supabase
      .from("user_roles")
      .select("*", { count: "exact", head: true })
      .eq("role", "student")
      .gte("created_at", weekAgoIso),
    // Judge deltas — judges table, status='active'.
    supabase.from("judges").select("*", { count: "exact", head: true }).eq("status", "active"),
    supabase
      .from("judges")
      .select("*", { count: "exact", head: true })
      .eq("status", "active")
      .gte("invited_at", weekAgoIso),
    // Open competitions.
    supabase.from("competitions").select("*", { count: "exact", head: true }).eq("status", "open"),
    supabase
      .from("competitions")
      .select("*", { count: "exact", head: true })
      .eq("status", "open")
      .gte("created_at", weekAgoIso),
    // Queues.
    supabase.from("company_inquiries").select("*", { count: "exact", head: true }).eq("status", "new"),
    getPendingAdminTeamCount(),
    supabase
      .from("coravu_calls")
      .select("*", { count: "exact", head: true })
      .eq("status", "scheduled")
      .gte("starts_at", nowIso)
      .lte("starts_at", inAWeekIso),
    supabase
      .from("competitions")
      .select("*", { count: "exact", head: true })
      .eq("status", "draft")
      .lte("created_at", weekAgoIso),
  ]);

  const totals = (Array.isArray(totalsRes.data) ? totalsRes.data[0] : totalsRes.data) as
    | Record<string, number>
    | null
    | undefined;

  return {
    total_students: Number(totals?.students ?? studentsAllRes.count ?? 0),
    total_active_judges: Number(totals?.active_judges ?? judgesAllRes.count ?? 0),
    total_open_competitions: Number(totals?.open_competitions ?? compsOpenRes.count ?? 0),
    new_students_7d: Number(studentsNewRes.count ?? 0),
    new_active_judges_7d: Number(judgesNewRes.count ?? 0),
    new_open_competitions_7d: Number(compsOpenNewRes.count ?? 0),
    inquiries_new: Number(inquiriesNewRes.count ?? 0),
    pending_team: Number(pendingTeam),
    discovery_calls_this_week: Number(callsThisWeekRes.count ?? 0),
    drafts_stale_gt_7d: Number(staleDraftsRes.count ?? 0),
  };
}

export type AdminActivityKind =
  | "inquiry"
  | "student_signup"
  | "competition_created"
  | "team_application";

export type AdminActivityRow = {
  id: string;
  kind: AdminActivityKind;
  ts: string;
  title: string;
  subtitle: string | null;
  href: string | null;
};

/**
 * Recent platform activity for the admin dashboard. Unions a small set of
 * recent rows (12 from each source), merges + sorts client-side, returns
 * the top `limit`. No new RPC — straight queries.
 */
export async function getRecentAdminActivity(limit = 12): Promise<AdminActivityRow[]> {
  const [inquiriesRes, studentsRes, compsRes, pendingTeam] = await Promise.all([
    supabase
      .from("company_inquiries")
      .select("id, name, channel, created_at")
      .order("created_at", { ascending: false })
      .limit(12),
    supabase
      .from("user_roles")
      .select("user_id, created_at")
      .eq("role", "student")
      .order("created_at", { ascending: false })
      .limit(12),
    supabase
      .from("competitions")
      .select("id, name, created_at, company_id")
      .order("created_at", { ascending: false })
      .limit(12),
    listAdminUsers({ status: "pending" }),
  ]);
  const inquiries = inquiriesRes.data;
  const students = studentsRes.data;
  const comps = compsRes.data;

  // Hydrate student names for the signup feed.
  const studentIds = ((students ?? []) as { user_id: string }[]).map((s) => s.user_id);
  const profileById = new Map<string, { full_name: string | null; email: string | null }>();
  if (studentIds.length > 0) {
    const { data: profiles } = await supabase
      .from("user_profiles")
      .select("id, full_name, email")
      .in("id", studentIds);
    for (const p of (profiles ?? []) as { id: string; full_name: string | null; email: string | null }[]) {
      profileById.set(p.id, { full_name: p.full_name, email: p.email });
    }
  }

  // Hydrate competition company names.
  const companyIds = Array.from(
    new Set(((comps ?? []) as { company_id: string }[]).map((c) => c.company_id))
  );
  const companyById = new Map<string, string>();
  if (companyIds.length > 0) {
    const { data: companies } = await supabase
      .from("companies")
      .select("id, name")
      .in("id", companyIds);
    for (const c of (companies ?? []) as { id: string; name: string }[]) {
      companyById.set(c.id, c.name);
    }
  }

  const rows: AdminActivityRow[] = [];

  for (const r of (inquiries ?? []) as { id: string; name: string; channel: string; created_at: string }[]) {
    rows.push({
      id: `inq:${r.id}`,
      kind: "inquiry",
      ts: r.created_at,
      title: `${r.name} submitted an inquiry`,
      subtitle: `Channel · ${r.channel}`,
      href: "/admin/inquiries",
    });
  }
  for (const r of (students ?? []) as { user_id: string; created_at: string }[]) {
    const p = profileById.get(r.user_id);
    const label = p?.full_name?.trim() || p?.email || "A student";
    rows.push({
      id: `stu:${r.user_id}`,
      kind: "student_signup",
      ts: r.created_at,
      title: `${label} signed up`,
      subtitle: "New student",
      href: `/admin/student/${r.user_id}`,
    });
  }
  for (const r of (comps ?? []) as { id: string; name: string; created_at: string; company_id: string }[]) {
    const cn = companyById.get(r.company_id);
    rows.push({
      id: `cmp:${r.id}`,
      kind: "competition_created",
      ts: r.created_at,
      title: `${cn ?? "A company"} created “${r.name}”`,
      subtitle: "New competition",
      href: `/admin/competitions`,
    });
  }
  for (const r of pendingTeam ?? []) {
    if (!r.role_assigned_at) continue;
    rows.push({
      id: `team:${r.id}`,
      kind: "team_application",
      ts: r.role_assigned_at,
      title: `${r.full_name ?? r.email ?? "Someone"} applied as ${r.role}`,
      subtitle: "Awaiting approval",
      href: "/admin/team",
    });
  }

  rows.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
  return rows.slice(0, limit);
}

export async function listUserSessions(userId: string): Promise<SessionRow[]> {
  const { data, error } = await supabase.rpc("admin_list_user_sessions", { p_user_id: userId });
  if (error) return [];
  const raw = unwrapRpcRows<Record<string, unknown>>(data);
  return raw.map((r) => ({
    session_id: String(r.session_id ?? r.id ?? ""),
    created_at: String(r.created_at ?? ""),
    updated_at: String(r.updated_at ?? r.created_at ?? ""),
    user_agent: (r.user_agent as string | null | undefined) ?? null,
    ip: (r.ip as string | null | undefined) ?? null,
  })).filter((s) => s.session_id);
}
